/* src/components/TrackingConfig.jsx
 * Tracking configuration component - separate file for clean code organization
 * Handles all tracking pixel setup, validation, and testing
 */

import React, { useState, useEffect, useRef } from 'react';
import { injectMetaPixel, fireMetaPixel, injectGTM, pushGTMConversionEvent } from '../utils/tracking';
import HelpPopover from './HelpPopover';
import GuidedManualStep from './GuidedManualStep';
import { useToast } from '../contexts/ToastContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { googleAdsApi, trackingApi } from '../utils/api';

function formatCustomerId(id) {
  const s = String(id).replace(/\D/g, '');
  return s.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
}

const PLATFORMS = [
  { id: 'google_ads', name: 'Google Ads', icon: '🔵' },
  { id: 'meta', name: 'Meta (Facebook/Instagram)', icon: '📘' },
  { id: 'linkedin', name: 'LinkedIn', icon: '💼' },
  { id: 'tiktok', name: 'TikTok', icon: '🎵' },
];

const STATUS_CONFIG = {
  verified:         { label: '✓ Verified',                                tone: 'green' },
  offline_armed:    { label: '✓ Closed-deal reporting armed',             tone: 'green' },
  pixel_only:       { label: "⚠ Pixel only: closed deals won't report", tone: 'amber' },
  needs_connection: { label: '⚠ Needs connection',                        tone: 'amber' },
  error:            { label: '✕ Not working',                             tone: 'red'   },
};

const TONE_CLASS = {
  green: 'fb-tone-green',
  amber: 'fb-tone-amber',
  red: 'fb-tone-red',
  grey: 'fb-tone-grey',
};

function getTrackingsSummary(testResults, pixels) {
  if (!pixels || pixels.length === 0) return null;

  const tested = pixels.filter((p) => testResults[p.platform]);
  if (tested.length === 0) {
    return { text: 'Run Test on each platform to verify your tracking.', tone: 'grey' };
  }

  const GREEN = new Set(['verified', 'offline_armed']);
  const allGreen = tested.length === pixels.length && tested.every((p) => GREEN.has(testResults[p.platform].status));

  if (allGreen) {
    const names = tested.map((p) => {
      const platform = PLATFORMS.find((pl) => pl.id === p.platform);
      return platform?.name.split(' ')[0] || p.platform;
    }).join(' + ');
    return { text: `✓ Tracking verified: closed-deal reporting armed for ${names}.`, tone: 'green' };
  }

  const parts = pixels.map((p) => {
    const platform = PLATFORMS.find((pl) => pl.id === p.platform);
    const shortName = platform?.name.split(' ')[0] || p.platform;
    const result = testResults[p.platform];
    if (!result) return `${shortName} (not tested)`;
    const cfg = STATUS_CONFIG[result.status];
    if (!cfg) return `${shortName} ?`;
    if (cfg.tone === 'green') return `${shortName} ✓`;
    if (cfg.tone === 'amber') {
      const issue = result.status === 'pixel_only' ? "pixel only, closed deals won't report" : 'needs connection';
      return `${shortName} ⚠ ${issue}`;
    }
    return `${shortName} ✕ not working`;
  });

  const hasError = tested.some((p) => testResults[p.platform]?.status === 'error');
  return { text: parts.join(' · '), tone: hasError ? 'red' : 'amber' };
}

const PLATFORM_INSTRUCTIONS = {
  google_ads: {
    simple: 'Enter your Conversion ID and Conversion Label',
    where: 'Google Ads → Tools & Settings → Conversions',
    exampleConversionId: 'AW-123456789',
    exampleConversionLabel: 'abc123',
  },
  meta: {
    simple: 'Paste your Pixel ID to start tracking. Add your CAPI token below to arm closed-deal reporting.',
    where: 'Events Manager → your pixel → copy the 15–16 digit number',
    whereUrl: 'https://business.facebook.com/events_manager2/',
    examplePixelId: '123456789012345',
  },
  linkedin: {
    simple: 'Enter your Conversion ID and Partner ID',
    where: 'Campaign Manager → Data → Signals Manager',
    exampleConversionId: '12345',
    examplePartnerId: '123456',
  },
  tiktok: {
    simple: 'Enter your Pixel ID (19 characters)',
    where: 'TikTok Ads Manager → Events → Web Events',
    examplePixelId: 'C1234567890123456789',
    helpText: '💡 Add Access Token below to test with one click',
  },
};

function AdsConnectionBanner({ adsStatus, adsStatusLoading, adsConnecting, onConnect, compact = false }) {
  const mb = compact ? '0.75rem' : '1rem';

  if (adsStatusLoading) {
    return (
      <div className="fb-callout info" style={{ fontSize: '0.85rem', marginBottom: mb, lineHeight: '1.5' }}>
        Checking Google Ads connection…
      </div>
    );
  }

  if (!adsStatus || adsStatus.error) return null;

  if (adsStatus.connected) {
    return (
      <div className={`fb-callout ${TONE_CLASS.green}`} style={{ fontSize: '0.85rem', marginBottom: mb, lineHeight: '1.5' }}>
        Connected to Google Ads, account{' '}
        <strong className="mono">{formatCustomerId(adsStatus.customerId)}</strong>.
        {' '}Closed-won deals will report automatically.
      </div>
    );
  }

  return (
    <div className={`fb-callout ${TONE_CLASS.amber}`} style={{ fontSize: '0.85rem', marginBottom: mb, lineHeight: '1.5', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
      <span style={{ flex: 1 }}>Google Ads not connected: closed-deal reporting won&apos;t fire.</span>
      <button
        type="button"
        className="inbox-pill-btn-primary"
        onClick={onConnect}
        disabled={adsConnecting}
        style={{ fontSize: '0.8rem', opacity: adsConnecting ? 0.6 : 1, cursor: adsConnecting ? 'not-allowed' : 'pointer' }}
      >
        {adsConnecting ? 'Redirecting…' : 'Connect Google Ads'}
      </button>
    </div>
  );
}

const GREEN_TV = new Set(['verified', 'offline_armed']);
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function buildPersistedResult(entry) {
  const status = entry.status;
  const ts = entry.at?.toDate?.() ?? null;
  const isGreen = GREEN_TV.has(status);
  const lastLabel = ts
    ? ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;
  let message = '';
  let fixAction = null;
  if (status === 'verified') {
    message = 'Verified via weekly sweep.';
  } else if (status === 'offline_armed') {
    message = 'Closed-deal reporting armed (offline conversion, pixel fires on form submit).';
  } else if (status === 'needs_connection') {
    message = 'Token connection lost or expired during weekly sweep.';
    fixAction = 'Re-add your CAPI access token in Advanced settings, then run Test it yourself.';
  } else {
    message = 'Test failed during last weekly sweep.';
    fixAction = 'Re-check your pixel IDs and token, then run Test it yourself.';
  }
  return {
    status,
    success: isGreen,
    timestamp: ts,
    message,
    fixAction,
    _fromPersisted: true,
    _lastLabel: lastLabel,
  };
}

export default function TrackingConfig({ formId, formName, trackingPixels = [], onSave, chatConfigId, trackingVerification = {}, autoVerify = false }) {
  const { showToast } = useToast();
  const { selectedWorkspaceId } = useWorkspace();
  const [pixels, setPixels] = useState(trackingPixels || []);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editIndex, setEditIndex] = useState(null); // null = add mode, number = editing that row
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [implementationMethod, setImplementationMethod] = useState('direct');
  const [formData, setFormData] = useState({
    conversionId: '',
    conversionLabel: '',
    pixelId: '',
    partnerId: '',
    accessToken: '',
    eventName: '',
    gtmContainerId: '',
    autoUpdate: false,
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [testingPixel, setTestingPixel] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [devInstructionsFor, setDevInstructionsFor] = useState(null); // index of pixel to show dev instructions for
  const [copiedDevInstructions, setCopiedDevInstructions] = useState(false);
  const [adsStatus, setAdsStatus] = useState(null);       // null = not yet fetched
  const [adsStatusLoading, setAdsStatusLoading] = useState(false);
  const [adsConnecting, setAdsConnecting] = useState(false);
  // Auto-verify (wizard only): fire the verify test at most once per platform per mount.
  const autoVerifyAttempted = useRef(new Set());
  // Guided manual step — toggle "Walk me through it" per platform
  const [showMetaGuide, setShowMetaGuide] = useState(false);
  const [showGoogleGuide, setShowGoogleGuide] = useState(false);
  const [showLinkedInGuide, setShowLinkedInGuide] = useState(false);
  const [showTiktokGuide, setShowTiktokGuide] = useState(false);
  const [showTiktokTokenGuide, setShowTiktokTokenGuide] = useState(false);

  const HELP_CONTENT = {
    google_ads_conversionId: { title: 'Where to find Conversion ID', content: 'Go to Google Ads → Tools & Settings → Conversions. Click your conversion action. The Conversion ID is in the tag setup (format: AW-123456789).', linkUrl: 'https://ads.google.com', linkText: 'Open Google Ads →' },
    google_ads_conversionLabel: { title: 'Where to find Conversion Label', content: 'Same place as Conversion ID. The label is the second part of the send_to value (e.g. AbCdEfGh).', linkUrl: 'https://ads.google.com', linkText: 'Open Google Ads →' },
    meta_pixelId: { title: 'Where to find Pixel ID', content: 'Go to Events Manager. Click your pixel. Copy the 15–16 digit number shown under the pixel name (not the Test Event Code).', linkUrl: 'https://business.facebook.com/events_manager2', linkText: 'Open Events Manager →' },
    meta_accessToken: { title: 'Where to find Access Token', content: 'Events Manager → your pixel → Settings → Conversions API → Generate access token. Enables server-side tracking for better accuracy.', linkUrl: 'https://business.facebook.com/events_manager2', linkText: 'Open Events Manager →' },
    tiktok_pixelId: { title: 'Where to find Pixel ID', content: 'TikTok Ads Manager → Events Manager → Web Events. Create or select a pixel. The ID is 19 characters.', linkUrl: 'https://ads.tiktok.com', linkText: 'Open TikTok Ads Manager →' },
    tiktok_accessToken: { title: 'Where to find Access Token', content: 'Events Manager → your pixel → Settings → Events API. Generate token for server-side tracking.', linkUrl: 'https://ads.tiktok.com', linkText: 'Open TikTok Ads Manager →' },
    linkedin_conversionId: { title: 'Where to find Conversion ID', content: 'Campaign Manager → Account Assets → Conversion Tracking. Create or select a conversion.', linkUrl: 'https://www.linkedin.com/campaignmanager', linkText: 'Open Campaign Manager →' },
    linkedin_partnerId: { title: 'Where to find Partner ID', content: 'Same as Insight Tag setup. Format: 6-digit number.', linkUrl: 'https://www.linkedin.com/campaignmanager', linkText: 'Open Campaign Manager →' },
    gtm_containerId: { title: 'Where to find GTM Container ID', content: 'Tag Manager → your container → copy the Container ID (format: GTM-XXXXXX).', linkUrl: 'https://tagmanager.google.com/', linkText: 'Open Tag Manager →' },
  };

  useEffect(() => {
    setPixels(trackingPixels || []);
  }, [trackingPixels]);

  // Auto-verify (wizard only): run the verify test once per configured platform
  // that has no persisted status yet. Ref-guarded so it never double-fires, and
  // sequential/staggered so we don't fire every platform at once.
  useEffect(() => {
    if (!autoVerify || !pixels || pixels.length === 0) return;
    let cancelled = false;
    (async () => {
      for (const pixel of pixels) {
        const platform = pixel.platform;
        if (autoVerifyAttempted.current.has(platform)) continue;   // once per mount
        if (trackingVerification?.[platform]?.status) continue;     // skip already-persisted
        if (testResults[platform]) continue;                       // skip already-tested in session
        autoVerifyAttempted.current.add(platform);
        if (cancelled) break;
        try { await handleTestPixel(pixel); } catch (_) { /* honest badge handles failures */ }
        await new Promise((r) => setTimeout(r, 600));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoVerify, pixels, trackingVerification]);

  // Fetch workspace Google Ads connection status once, whenever Google is relevant
  useEffect(() => {
    const googleRelevant =
      selectedPlatform === 'google_ads' ||
      (pixels || []).some((p) => p.platform === 'google_ads');
    if (!googleRelevant || !selectedWorkspaceId) return;
    setAdsStatus(null);
    setAdsStatusLoading(true);
    googleAdsApi.status(selectedWorkspaceId)
      .then((res) => setAdsStatus(res))
      .catch(() => setAdsStatus({ error: true }))
      .finally(() => setAdsStatusLoading(false));
  }, [selectedPlatform, pixels, selectedWorkspaceId]);

  async function handleAdsConnect() {
    setAdsConnecting(true);
    try {
      const res = await googleAdsApi.startConnect(selectedWorkspaceId);
      if (res.url) {
        // TODO: dynamic callback return target — after connecting, redirect back to this form instead of /advanced-settings
        window.location.href = res.url;
      }
    } catch (e) {
      // silent — user stays on page
    } finally {
      setAdsConnecting(false);
    }
  }

  // AI-powered event name suggestion based on form name
  function suggestEventName() {
    if (!formName) return 'Lead';
    
    // Clean form name and create event name
    const cleaned = formName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .substring(0, 30);
    
    return cleaned ? `Lead_${cleaned}` : 'Lead';
  }

  // Validate pixel code based on platform and implementation method (Direct vs GTM)
  function validatePixelCode(platform, data, implMethod) {
    const errors = {};
    const method = implMethod || 'direct';

    if (method === 'gtm') {
      if (!data.gtmContainerId || !data.gtmContainerId.trim().match(/^GTM-[A-Z0-9]+$/i)) {
        errors.gtmContainerId = 'GTM Container ID should be like GTM-XXXXXX';
      }
      return errors;
    }

    switch (platform) {
      case 'google_ads':
        if (data.conversionId && !data.conversionId.match(/^AW-\d{9,11}$/)) {
          errors.conversionId = 'Conversion ID format should be AW-XXXXXXXXX (9-11 digits)';
        }
        if (data.conversionId && !data.conversionLabel?.trim()) {
          errors.conversionLabel = 'Provide a Conversion Label to go with this ID';
        }
        if (data.conversionLabel?.trim() && !data.conversionId) {
          errors.conversionId = 'Provide a Conversion ID to go with this Label';
        }
        break;

      case 'meta':
        if (!data.pixelId || !data.pixelId.match(/^\d{15,16}$/)) {
          errors.pixelId = 'Pixel ID should be 15-16 digits';
        }
        break;

      case 'linkedin':
        if (!data.conversionId && !data.pixelId) {
          errors.conversionId = 'Either Conversion ID + Partner ID or Insight Tag code is required';
        }
        if (data.conversionId && !data.partnerId) {
          errors.partnerId = 'Partner ID is required when using Conversion ID';
        }
        break;

      case 'tiktok':
        if (!data.pixelId || data.pixelId.length !== 19) {
          errors.pixelId = 'Pixel ID should be exactly 19 characters';
        }
        break;
    }

    const googleAdsConnectionOnly = platform === 'google_ads' && !data.conversionId && !data.conversionLabel?.trim();
    if (platform !== 'meta' && !googleAdsConnectionOnly && (!data.eventName || data.eventName.trim().length === 0)) {
      errors.eventName = 'Event name is required';
    }

    return errors;
  }

  function handlePlatformChange(platform) {
    setSelectedPlatform(platform);
    setShowAdvanced(false);
    const defaultEvent = platform === 'tiktok' ? 'SubmitForm' : (platform === 'meta' ? 'Lead' : suggestEventName());
    setFormData({
      conversionId: '',
      conversionLabel: '',
      pixelId: '',
      partnerId: '',
      accessToken: '',
      eventName: defaultEvent,
      gtmContainerId: '',
      autoUpdate: false,
    });
    setValidationErrors({});
  }

  function handleEditPixel(pixel, index) {
    setEditIndex(index);
    setSelectedPlatform(pixel.platform);
    setImplementationMethod(pixel.implementationMethod || 'direct');
    setShowAdvanced(false);
    setFormData({
      conversionId: pixel.conversionId || '',
      conversionLabel: pixel.conversionLabel || '',
      pixelId: pixel.pixelId || '',
      partnerId: pixel.partnerId || '',
      accessToken: '',           // never prefill the secret
      eventName: pixel.eventName || '',
      gtmContainerId: pixel.gtmContainerId || '',
      autoUpdate: false,
    });
    setValidationErrors({});
    setShowAddForm(true);
  }

  function handleInputChange(field, value) {
    setFormData({ ...formData, [field]: value });
    // Clear validation error for this field
    if (validationErrors[field]) {
      const newErrors = { ...validationErrors };
      delete newErrors[field];
      setValidationErrors(newErrors);
    }
  }

  async function handleAddPixel() {
    const errors = validatePixelCode(selectedPlatform, formData, implementationMethod);

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Platforms that support server-side CAPI tokens
    const capiPlatforms = ['meta', 'tiktok', 'linkedin'];
    const hasNewToken = !!(formData.accessToken && capiPlatforms.includes(selectedPlatform));

    // Store the token securely BEFORE writing the form doc.
    // If this fails, abort entirely — never write hasAccessToken:true without a
    // token actually in the secret store. An orphaned secret from the reverse
    // failure (store succeeded, form write failed) is harmless.
    if (hasNewToken) {
      // Guard: token storage requires a saved form/config ID.
      // A new unsaved form (formId='new') cannot store a secret yet.
      if (!chatConfigId && (!formId || formId === 'new')) {
        showToast('Save the form first, then add your tracking token.', 'error');
        return;
      }
      try {
        const result = chatConfigId
          ? await trackingApi.setChatSecret(chatConfigId, selectedPlatform, formData.accessToken)
          : await trackingApi.setFormSecret(formId, selectedPlatform, formData.accessToken);
        if (!result.success) {
          throw new Error(result.error || 'Token storage failed');
        }
      } catch (err) {
        const msg = err.message || 'Unknown error';
        console.error('[TrackingConfig] setTrackingSecret failed:', msg);
        showToast(`Could not save token securely, tracking not saved. (${msg})`, 'error');
        return;
      }
    }

    const newPixel = {
      platform: selectedPlatform,
      conversionId: formData.conversionId || undefined,
      conversionLabel: formData.conversionLabel || undefined,
      pixelId: formData.pixelId || undefined,
      partnerId: formData.partnerId || undefined,
      // Token stored in form_tracking_secrets (or chat_tracking_secrets) — never on the form doc.
      // hasAccessToken:true tells the server a token exists without exposing it to
      // the public-readable forms collection.
      hasAccessToken: hasNewToken ? true : undefined,
      eventName: selectedPlatform === 'meta' ? 'Lead' : (selectedPlatform === 'tiktok' ? (formData.eventName || 'SubmitForm') : (formData.eventName || 'Lead')),
      implementationMethod: implementationMethod || 'direct',
      gtmContainerId: implementationMethod === 'gtm' ? (formData.gtmContainerId || '').trim() : undefined,
      autoUpdate: false,
      status: 'active',
      lastUpdated: new Date(),
    };
    // Firestore rejects undefined values — strip them so the pixel persists cleanly
    Object.keys(newPixel).forEach((k) => newPixel[k] === undefined && delete newPixel[k]);

    // Determine which existing row to replace (edit mode → that row; add mode → same platform if present).
    const targetIndex = editIndex !== null
      ? editIndex
      : pixels.findIndex((p) => p.platform === selectedPlatform);

    // When no new token was typed, carry the existing stored-token flag so the secret isn't orphaned.
    if (!hasNewToken && targetIndex >= 0 && pixels[targetIndex].hasAccessToken) {
      newPixel.hasAccessToken = true;
    }

    const updatedPixels = targetIndex >= 0
      ? pixels.map((p, i) => (i === targetIndex ? newPixel : p))
      : [...pixels, newPixel];
    setPixels(updatedPixels);

    if (onSave) {
      await onSave(updatedPixels);
    }

    // Auto-test: pass the in-memory token so the immediate post-add test hits CAPI.
    // Subsequent "Test Now" clicks will look up the secret store via the backend.
    if (hasNewToken && (selectedPlatform === 'meta' || selectedPlatform === 'tiktok')) {
      const pixelForTest = { ...newPixel, accessToken: formData.accessToken };
      setTimeout(() => {
        handleTestPixel(pixelForTest);
      }, 1000);
    }

    // Reset form
    setShowAddForm(false);
    setEditIndex(null);
    setSelectedPlatform('');
    setImplementationMethod('direct');
    setFormData({
      conversionId: '',
      conversionLabel: '',
      pixelId: '',
      partnerId: '',
      accessToken: '',
      eventName: '',
      gtmContainerId: '',
      autoUpdate: false,
    });
    setValidationErrors({});
  }

  async function handleTestPixel(pixel) {
    setTestingPixel(pixel.platform);
    const method = pixel.implementationMethod || 'direct';

    // GTM: push test event to dataLayer and show success
    if (method === 'gtm' && pixel.gtmContainerId) {
      try {
        injectGTM(pixel.gtmContainerId);
        await new Promise((r) => setTimeout(r, 800));
        pushGTMConversionEvent(pixel, formId, formName, { test: true });
        setTestResults((prev) => ({
          ...prev,
          [pixel.platform]: {
            status: 'pixel_only',
            success: true,
            timestamp: new Date(),
            message: 'Test event pushed to dataLayer. Open GTM Preview (or your container) to see the form_submit event.',
            fixAction: 'Configure a GTM tag to forward this event to your ad platform for full server-side verification.',
            verificationUrl: 'https://tagmanager.google.com/',
          },
        }));
      } catch (e) {
        setTestResults((prev) => ({
          ...prev,
          [pixel.platform]: {
            status: 'error',
            success: false,
            timestamp: new Date(),
            message: `❌ ${e?.message || 'Could not push test event.'}`,
          },
        }));
      } finally {
        setTestingPixel(null);
      }
      return;
    }

    // Meta without any token (neither in-memory nor in secret store): client-side test only.
    // If hasAccessToken is true, a stored token exists — fall through to the backend CAPI test.
    if (pixel.platform === 'meta' && !pixel.accessToken && !pixel.hasAccessToken && pixel.pixelId) {
      try {
        injectMetaPixel(pixel.pixelId);
        await new Promise((r) => setTimeout(r, 600));
        fireMetaPixel(pixel.pixelId, 'Lead', {}, null);
        setTestResults((prev) => ({
          ...prev,
          [pixel.platform]: {
            status: 'pixel_only',
            success: true,
            timestamp: new Date(),
            message: 'Browser pixel fired. Check Events Manager → Test Events in 1–2 minutes.',
            fixAction: 'Add a CAPI Access Token (Advanced settings) to arm server-side and closed-deal reporting.',
            verificationUrl: `https://business.facebook.com/events_manager2/list/pixel/${pixel.pixelId}`,
          },
        }));
      } catch (e) {
        setTestResults((prev) => ({
          ...prev,
          [pixel.platform]: {
            status: 'error',
            success: false,
            timestamp: new Date(),
            message: `❌ ${e?.message || 'Could not send test event.'}`,
          },
        }));
      } finally {
        setTestingPixel(null);
      }
      return;
    }

    try {
      // Build pixel config based on platform
      const pixelConfig = {
        pixelId: pixel.pixelId,
        accessToken: pixel.accessToken,
        eventName: pixel.eventName || 'Lead',
        conversionId: pixel.conversionId,
        conversionLabel: pixel.conversionLabel,
        partnerId: pixel.partnerId,
        testEventCode: pixel.testEventCode,
      };

      // Include auth header so the backend can look up the secret store for
      // saved pixels where pixelConfig.accessToken is absent (Phase 2+).
      const result = await trackingApi.testPixel({
        platform: pixel.platform,
        pixelConfig,
        formId,
        ...(chatConfigId ? { chatConfigId } : {}),
      });

      setTestResults((prev) => ({
        ...prev,
        [pixel.platform]: {
          status: result.status,
          success: result.success,
          timestamp: new Date(),
          message: result.message,
          fixAction: result.fixAction,
          verificationUrl: result.verificationUrl,
        },
      }));
    } catch (error) {
      let errorMessage = '';
      
      if (error.message.includes('Access Token')) {
        errorMessage = '❌ No Access Token. Add one above to test here, or test using Meta Pixel Helper browser extension.';
      } else if (error.message.includes('Function not found') || error.message.includes('404')) {
        errorMessage = '⏳ Testing function is still deploying. Wait 2-3 minutes and try again.';
      } else if (error.message.includes('Pixel ID')) {
        errorMessage = '❌ Invalid Pixel ID. Check that you copied the correct number from Events Manager.';
      } else {
        errorMessage = `❌ ${error.message || 'Test failed. Check your Pixel ID and try again.'}`;
      }

      setTestResults((prev) => ({
        ...prev,
        [pixel.platform]: {
          status: 'error',
          success: false,
          timestamp: new Date(),
          message: errorMessage,
        },
      }));
    } finally {
      setTestingPixel(null);
    }
  }

  function handleRemovePixel(index) {
    const updatedPixels = pixels.filter((_, i) => i !== index);
    setPixels(updatedPixels);
    
    if (onSave) {
      onSave(updatedPixels);
    }
  }

  function getDeveloperInstructions(pixel, platformName) {
    const formLabel = formName || 'this form';
    const where = `gocta.ai → Forms → "${formLabel}" → Tracking tab`;
    const method = pixel.implementationMethod || 'direct';
    let line = '';
    if (method === 'gtm') {
      line = `GTM: Container ID ${pixel.gtmContainerId || '-'}. We push event "form_submit" to dataLayer; set up tags in GTM to listen for it.`;
    } else if (pixel.platform === 'meta') {
      line = `Meta (Facebook/Instagram): Pixel ID ${pixel.pixelId}. Event: Lead.`;
    } else if (pixel.platform === 'google_ads') {
      line = `Google Ads: Conversion ID ${pixel.conversionId}, Conversion Label ${pixel.conversionLabel}. Event: ${pixel.eventName || 'conversion'}.`;
    } else if (pixel.platform === 'linkedin') {
      line = `LinkedIn: Conversion ID ${pixel.conversionId || '-'}, Partner ID ${pixel.partnerId || '-'}. Event: ${pixel.eventName || 'Lead'}.`;
    } else if (pixel.platform === 'tiktok') {
      line = `TikTok: Pixel ID ${pixel.pixelId}. Event: ${pixel.eventName || 'SubmitForm'}.`;
    } else {
      line = `${platformName}: see Tracking tab for IDs.`;
    }
    return `Instructions for your developer: ${platformName}\n\nForm: ${formLabel}\nWhere to add: ${where}\n\nAdd this platform:\n• ${line}\n\nWe fire the conversion when the form is submitted. No code changes on your site. When you've added this, the form owner can run "Test Now" in the Tracking tab to verify.`;
  }

  async function copyDeveloperInstructions(pixel, index) {
    const platform = PLATFORMS.find(p => p.id === pixel.platform);
    const text = getDeveloperInstructions(pixel, platform?.name || pixel.platform);
    try {
      await navigator.clipboard.writeText(text);
      setCopiedDevInstructions(true);
      setTimeout(() => setCopiedDevInstructions(false), 2000);
    } catch (_) {
      showToast('Copy failed, see instructions in the developer section below.', 'error');
    }
  }

  function getAllDeveloperInstructions() {
    const formLabel = formName || 'this form';
    const where = `gocta.ai → Forms → "${formLabel}" → Tracking tab`;
    const method = (p) => p.implementationMethod || 'direct';
    const lines = pixels.map((pixel) => {
      const platform = PLATFORMS.find(p => p.id === pixel.platform);
      const name = platform?.name || pixel.platform;
      if (method(pixel) === 'gtm') return `• GTM: Container ID ${pixel.gtmContainerId || '-'}. We push "form_submit" to dataLayer; set up tags in GTM.`;
      if (pixel.platform === 'meta') return `• Meta (Facebook/Instagram): Pixel ID ${pixel.pixelId}. Event: Lead.`;
      if (pixel.platform === 'google_ads') return `• Google Ads: Conversion ID ${pixel.conversionId}, Conversion Label ${pixel.conversionLabel}. Event: ${pixel.eventName || 'conversion'}.`;
      if (pixel.platform === 'linkedin') return `• LinkedIn: Conversion ID ${pixel.conversionId || '-'}, Partner ID ${pixel.partnerId || '-'}. Event: ${pixel.eventName || 'Lead'}.`;
      if (pixel.platform === 'tiktok') return `• TikTok: Pixel ID ${pixel.pixelId}. Event: ${pixel.eventName || 'SubmitForm'}.`;
      return `• ${name}: see Tracking tab for IDs.`;
    });
    return `Instructions for your developer: conversion tracking\n\nForm: ${formLabel}\nWhere to add: ${where}\n\nAdd these platforms:\n${lines.join('\n')}\n\nWe fire the conversion when the form is submitted. No code changes on your site. When you've added these, the form owner can run "Test Now" in the Tracking tab to verify each one.`;
  }

  async function copyAllDeveloperInstructions() {
    const text = getAllDeveloperInstructions();
    try {
      await navigator.clipboard.writeText(text);
      setCopiedDevInstructions(true);
      setTimeout(() => setCopiedDevInstructions(false), 2000);
    } catch (_) {
      showToast('Copy failed, see instructions in the developer section below.', 'error');
    }
  }

  const instructions = selectedPlatform ? PLATFORM_INSTRUCTIONS[selectedPlatform] : null;

  return (
    <div className="fb-tracking-root">
      <div className="fb-callout info" style={{ padding: '1.5rem', marginBottom: '2rem' }}>
        <h3 className="fb-subsection-title" style={{ marginBottom: '0.5rem' }}>Conversion Tracking</h3>
        <p className="fb-muted" style={{ marginBottom: '0.75rem', fontSize: '0.95rem' }}>
          Track form submissions in Google Ads, Meta, LinkedIn, and TikTok. Test your setup with one click before running ads.
        </p>
        <div className="fb-callout tip" style={{ fontSize: '0.9rem' }}>
          <strong>How it works:</strong> We fire the conversion when someone submits your form. <strong>No thank-you page and no code on the submit button</strong>. Add your tracking IDs below and we signal Meta, Google, etc. automatically on submit. If a developer adds them for you, run <strong>Test Now</strong> on each platform here to confirm it’s set up correctly.
        </div>
      </div>

      {/* Step indicator */}
      <div className="fb-muted" style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
        <strong>Steps:</strong> 1. Add IDs below → 2. Run Test to verify
      </div>

      {/* TODO: clicks-captured light (reads recent leads for gclid/fbclid presence) — separate query, follow-up task */}
      {/* TODO (second pass): persist testResults to form doc as form.trackingVerification[platform] = { status, at }
          then surface a compact pill in FormBuilderList and a green row in the Dashboard Quick Start checklist */}

      {/* Per-form tracking summary — honest at-a-glance status */}
      {pixels.length > 0 && (() => {
        const summary = getTrackingsSummary(testResults, pixels);
        if (!summary) return null;
        return (
          <div className={`fb-callout ${TONE_CLASS[summary.tone]}`} style={{ fontSize: '0.95rem', fontWeight: '500', marginBottom: '1.5rem' }}>
            {summary.text}
          </div>
        );
      })()}

      {/* Existing Tracking Pixels — per-platform: Test yourself or Invite developer, + verification status */}
      {pixels.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ marginTop: 0, marginBottom: '0.5rem', fontSize: '1rem' }}>Configured Platforms</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <p style={{ fontSize: '0.9rem', color: 'var(--muted)', margin: 0 }}>
              Test it yourself or send instructions to a developer. Run <strong>Test Now</strong> after setup to confirm it works.
            </p>
            <button
              type="button"
              className={copiedDevInstructions ? 'inbox-pill-btn-primary' : 'inbox-pill-btn'}
              onClick={copyAllDeveloperInstructions}
              style={{ fontSize: '0.85rem' }}
            >
              {copiedDevInstructions ? 'Copied all' : 'Copy instructions for all platforms'}
            </button>
          </div>
          {pixels.map((pixel, index) => {
            const platform = PLATFORMS.find(p => p.id === pixel.platform);
            const isTesting = testingPixel === pixel.platform;
            const showDevForThis = devInstructionsFor === index;

            // Fresh in-memory test wins; fall back to persisted sweep result (≤7 days).
            const inMemory = testResults[pixel.platform];
            const persistedEntry = trackingVerification?.[pixel.platform];
            const persistedAge = persistedEntry?.at?.toDate
              ? Date.now() - persistedEntry.at.toDate().getTime()
              : Infinity;
            const persistedFresh = persistedAge <= SEVEN_DAYS_MS;
            const testResult = inMemory ?? (persistedEntry && persistedFresh ? buildPersistedResult(persistedEntry) : null);

            const statusConfig = testResult
              ? (STATUS_CONFIG[testResult.status] ?? { label: '? Unknown', tone: 'grey' })
              : null;
            const toneClass = statusConfig ? TONE_CLASS[statusConfig.tone] : TONE_CLASS.grey;
            const isGreen = statusConfig?.tone === 'green';

            return (
              <div
                key={index}
                className="fb-pixel-row"
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '1.2rem' }}>{platform?.icon || '📊'}</span>
                      <strong style={{ fontSize: '1rem' }}>{platform?.name || pixel.platform}</strong>
                      <span className={`fb-status-chip ${toneClass}`}>
                        {statusConfig ? statusConfig.label : 'Not tested yet'}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
                      <div>Method: <strong>{(pixel.implementationMethod || 'direct') === 'gtm' ? 'GTM' : 'Direct'}</strong></div>
                      {(pixel.implementationMethod || 'direct') === 'gtm' && pixel.gtmContainerId && (
                        <div>GTM Container: <code className="fb-code">{pixel.gtmContainerId}</code></div>
                      )}
                      <div>Event: <strong>{pixel.platform === 'meta' ? 'Lead' : (pixel.eventName || '-')}</strong></div>
                      {pixel.conversionId && <div>Conversion ID: <code className="fb-code">{pixel.conversionId}</code></div>}
                      {pixel.pixelId && <div>Pixel ID: <code className="fb-code">{pixel.pixelId}</code></div>}
                      {(pixel.hasAccessToken || pixel.accessToken) && ['meta', 'tiktok', 'linkedin'].includes(pixel.platform) && (
                        <div>CAPI token: <strong style={{ color: '#1b5e20' }}>🔒 Secured</strong></div>
                      )}
                    </div>

                    {/* Google Ads workspace connection status — primary closed-deal reporting indicator */}
                    {pixel.platform === 'google_ads' && (
                      <AdsConnectionBanner
                        adsStatus={adsStatus}
                        adsStatusLoading={adsStatusLoading}
                        adsConnecting={adsConnecting}
                        onConnect={handleAdsConnect}
                        compact
                      />
                    )}

                    {!isGreen && !testResult && (
                      <p style={{ fontSize: '0.8rem', color: '#856404', margin: '0.25rem 0 0 0' }}>
                        Run Test Now to verify, or send instructions to your developer and check back here after they add it.
                      </p>
                    )}
                    {testResult && (
                      <div className={`fb-callout ${toneClass}`} style={{ marginTop: '0.75rem' }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: '500', marginBottom: '0.25rem' }}>
                          {testResult._fromPersisted
                            ? isGreen
                              ? `✓ ${testResult._lastLabel ? `re-checked weekly · last: ${testResult._lastLabel}` : 're-checked weekly'}`
                              : `⚠ Issue detected · last checked: ${testResult._lastLabel ?? '-'}`
                            : isGreen
                            ? '✅ Test Successful!'
                            : statusConfig?.tone === 'amber'
                            ? '⚠ Partial setup'
                            : '❌ Test Failed'}
                        </div>
                        <div className="fb-muted" style={{ fontSize: '0.85rem', marginBottom: (testResult.fixAction || testResult.verificationUrl) ? '0.5rem' : 0 }}>
                          {testResult.message}
                        </div>
                        {testResult.fixAction && (
                          <div className="fb-muted" style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
                            → Next: {testResult.fixAction}
                          </div>
                        )}
                        {testResult.verificationUrl && isGreen && (
                          <a href={testResult.verificationUrl} target="_blank" rel="noopener noreferrer" className="inbox-pill-btn-primary" style={{ display: 'inline-block', textDecoration: 'none', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                            🔗 View in Dashboard →
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="inbox-pill-btn-primary"
                        onClick={() => handleTestPixel(pixel)}
                        disabled={isTesting}
                        style={{ fontSize: '0.85rem', opacity: isTesting ? 0.55 : 1, cursor: isTesting ? 'not-allowed' : 'pointer' }}
                      >
                        {isTesting ? 'Testing...' : 'Test it yourself'}
                      </button>
                      <button
                        type="button"
                        className={showDevForThis ? 'inbox-pill-btn' : 'inbox-pill-btn-primary'}
                        onClick={() => setDevInstructionsFor(showDevForThis ? null : index)}
                        style={{ fontSize: '0.85rem' }}
                      >
                        Get instructions for developer
                      </button>
                      <button
                        type="button"
                        className="inbox-pill-btn"
                        onClick={() => handleEditPixel(pixel, index)}
                        style={{ fontSize: '0.85rem' }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="fb-btn-danger-sm"
                        onClick={() => handleRemovePixel(index)}
                        style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}
                      >
                        Remove
                      </button>
                    </div>
                    {showDevForThis && (
                      <div style={{ marginTop: '0.5rem', width: '100%', maxWidth: '420px', textAlign: 'left' }}>
                        <pre className="fb-dev-pre">
                          {getDeveloperInstructions(pixel, platform?.name || pixel.platform)}
                        </pre>
                        <button
                          type="button"
                          onClick={() => copyDeveloperInstructions(pixel, index)}
                          style={{
                            padding: '0.4rem 0.75rem',
                            background: copiedDevInstructions ? '#28a745' : '#0d6efd',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                          }}
                        >
                          {copiedDevInstructions ? 'Copied' : 'Copy instructions'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add New Platform */}
      {!showAddForm ? (
        <button
          type="button"
          className="inbox-pill-btn-primary"
          onClick={() => setShowAddForm(true)}
          style={{ fontSize: '0.9rem' }}
        >
          Add Tracking Platform
        </button>
      ) : (
        <div className="fb-callout info" style={{ padding: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h4 style={{ marginTop: 0, marginBottom: 0 }}>{editIndex !== null ? 'Edit Tracking Platform' : 'Add Tracking Platform'}</h4>
            <button
              onClick={() => {
                setShowAddForm(false);
                setEditIndex(null);
                setSelectedPlatform('');
                setImplementationMethod('direct');
                setFormData({
                  conversionId: '',
                  conversionLabel: '',
                  pixelId: '',
                  partnerId: '',
                  accessToken: '',
                  eventName: '',
                  gtmContainerId: '',
                  autoUpdate: false,
                });
                setValidationErrors({});
              }}
              className="inbox-pill-btn"
              style={{ fontSize: '0.85rem' }}
            >
              Cancel
            </button>
          </div>

          {/* Platform Selector */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Platform <span style={{ color: 'var(--danger)' }}>*</span>
            </label>
            <select
              value={selectedPlatform}
              onChange={(e) => handlePlatformChange(e.target.value)}
              disabled={editIndex !== null}
              className="fb-input" style={{ width: '100%', fontSize: '0.9rem',
                background: editIndex !== null ? '#f0f0f0' : undefined,
                cursor: editIndex !== null ? 'not-allowed' : undefined,
              }}
            >
              <option value="">Select a platform...</option>
              {PLATFORMS.map((platform) => {
                const alreadyAdded = editIndex === null && pixels.some((p) => p.platform === platform.id);
                return (
                  <option key={platform.id} value={platform.id}>
                    {platform.icon} {platform.name}{alreadyAdded ? ' (already added)' : ''}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Platform-specific setup card — collapsible "How to get these values" */}
          {instructions && (
            <div
              className="fb-callout tip" style={{ padding: '0.75rem', marginBottom: '1rem', fontSize: '0.9rem' }}
            >
              <div style={{ color: selectedPlatform === 'meta' ? '#1b5e20' : '#004085', marginBottom: '0.25rem' }}>
                {selectedPlatform === 'meta' && instructions.whereUrl ? (
                  <strong>How to get these values: <a href={instructions.whereUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#0d47a1', textDecoration: 'underline' }}>Open Events Manager</a> → click your pixel → copy the 15–16 digit number</strong>
                ) : selectedPlatform === 'google_ads' ? (
                  <strong>How to get these values: <a href="https://ads.google.com" target="_blank" rel="noopener noreferrer" style={{ color: '#0d47a1', textDecoration: 'underline' }}>Open Google Ads</a> → Tools & Settings → Conversions</strong>
                ) : selectedPlatform === 'tiktok' ? (
                  <strong>How to get these values: <a href="https://ads.tiktok.com" target="_blank" rel="noopener noreferrer" style={{ color: '#0d47a1', textDecoration: 'underline' }}>Open TikTok Ads Manager</a> → Events Manager → Web Events</strong>
                ) : selectedPlatform === 'linkedin' ? (
                  <strong>How to get these values: <a href="https://www.linkedin.com/campaignmanager" target="_blank" rel="noopener noreferrer" style={{ color: '#0d47a1', textDecoration: 'underline' }}>Open Campaign Manager</a> → Conversion Tracking</strong>
                ) : (
                  <strong>📍 {instructions.where}</strong>
                )}
              </div>
              <div style={{ color: 'var(--muted)' }}>
                {instructions.simple}
              </div>
            </div>
          )}

          {/* Platform-Specific Fields */}
          {selectedPlatform && (
            <>
              {/* Implementation Method: Direct or GTM (all platforms) */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  How to send events
                </label>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="implementationMethod"
                      value="direct"
                      checked={implementationMethod === 'direct'}
                      onChange={() => setImplementationMethod('direct')}
                    />
                    <span>Direct</span> <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>(we load the tag)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="implementationMethod"
                      value="gtm"
                      checked={implementationMethod === 'gtm'}
                      onChange={() => setImplementationMethod('gtm')}
                    />
                    <span>Google Tag Manager</span> <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>(we push to dataLayer)</span>
                  </label>
                </div>
              </div>

              {/* GTM Container ID — only when GTM selected */}
              {implementationMethod === 'gtm' && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem', fontWeight: '500' }}>
                    GTM Container ID <span style={{ color: 'var(--danger)' }}>*</span>
                    <HelpPopover {...HELP_CONTENT.gtm_containerId} />
                  </label>
                  <input
                    type="text"
                    value={formData.gtmContainerId}
                    onChange={(e) => handleInputChange('gtmContainerId', e.target.value)}
                    placeholder="GTM-XXXXXX"
                    className={`fb-input${validationErrors.gtmContainerId ? ' fb-input-error' : ''}`}
                  />
                  {validationErrors.gtmContainerId && (
                    <div className="fb-inline-error" style={{ marginTop: '0.25rem' }}>
                      {validationErrors.gtmContainerId}
                    </div>
                  )}
                  <p style={{ fontSize: '0.85rem', color: 'var(--muted)', marginTop: '0.5rem', marginBottom: 0 }}>
                    Get it: <a href="https://tagmanager.google.com/" target="_blank" rel="noopener noreferrer" className="today-link-accent">Tag Manager</a> → your container → copy the ID (e.g. GTM-ABC123).
                  </p>
                </div>
              )}

              {/* Direct-only fields (hidden when GTM) */}
              {implementationMethod === 'direct' && (
              <>
              {/* Google Ads Fields */}
              {selectedPlatform === 'google_ads' && (
                <>
                  {/* Workspace connection status + unified explainer */}
                  <AdsConnectionBanner
                    adsStatus={adsStatus}
                    adsStatusLoading={adsStatusLoading}
                    adsConnecting={adsConnecting}
                    onConnect={handleAdsConnect}
                  />
                  <p style={{ fontSize: '0.85rem', color: 'var(--muted)', margin: '0 0 1rem', lineHeight: '1.5' }}>
                    Two things, one place: optional Conversion ID/Label fire a conversion when the form is submitted; your connected Google Ads account reports the real value when you mark a deal Won. The connection is what competitors can't match.
                  </p>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Conversion ID (optional, fires a conversion on form submit)
                      <HelpPopover {...HELP_CONTENT.google_ads_conversionId} />
                      <span style={{ fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                        (Format: {instructions.exampleConversionId})
                      </span>
                    </label>
                    <input
                      type="text"
                      value={formData.conversionId}
                      onChange={(e) => handleInputChange('conversionId', e.target.value)}
                      placeholder={instructions.exampleConversionId}
                      className={`fb-input${validationErrors.conversionId ? ' fb-input-error' : ''}`}
                    />
                    {validationErrors.conversionId && (
                      <div className="fb-inline-error" style={{ marginTop: '0.25rem' }}>
                        {validationErrors.conversionId}
                      </div>
                    )}
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Conversion Label (optional)
                      <HelpPopover {...HELP_CONTENT.google_ads_conversionLabel} />
                    </label>
                    <input
                      type="text"
                      value={formData.conversionLabel}
                      onChange={(e) => handleInputChange('conversionLabel', e.target.value)}
                      placeholder={instructions.exampleConversionLabel}
                      className={`fb-input${validationErrors.conversionLabel ? ' fb-input-error' : ''}`}
                    />
                    {validationErrors.conversionLabel && (
                      <div className="fb-inline-error" style={{ marginTop: '0.25rem' }}>
                        {validationErrors.conversionLabel}
                      </div>
                    )}
                  </div>

                  {/* Guided walkthrough — shown when neither ID nor Label is filled in */}
                  {!formData.conversionId && !formData.conversionLabel && (
                    <div style={{ marginBottom: '1rem' }}>
                      <button
                        type="button"
                        onClick={() => setShowGoogleGuide((v) => !v)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          color: '#1a73e8',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          textDecoration: 'underline',
                        }}
                      >
                        {showGoogleGuide ? 'Hide walkthrough' : 'Walk me through it →'}
                      </button>
                      {showGoogleGuide && (
                        <GuidedManualStep
                          guideId="google_ads_conversion"
                          pixelConfig={{ platform: 'google_ads' }}
                          formId={formId}
                          onComplete={() => setShowGoogleGuide(false)}
                        />
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Meta Fields — Pixel ID + CAPI Access Token (arms closed-deal reporting) */}
              {selectedPlatform === 'meta' && (
                <>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Pixel ID <span style={{ color: 'var(--danger)' }}>*</span>
                    <HelpPopover {...HELP_CONTENT.meta_pixelId} />
                  </label>
                  <input
                    type="text"
                    value={formData.pixelId}
                    onChange={(e) => handleInputChange('pixelId', e.target.value)}
                    placeholder="Paste the 15–16 digit number from Events Manager"
                    className={`fb-input${validationErrors.pixelId ? ' fb-input-error' : ''}`}
                  />
                  {validationErrors.pixelId && (
                    <div className="fb-inline-error" style={{ marginTop: '0.25rem' }}>
                      {validationErrors.pixelId}
                    </div>
                  )}
                  <p style={{ fontSize: '0.85rem', color: '#2e7d32', marginTop: '0.5rem', marginBottom: 0 }}>
                    ✓ Form submissions will be tracked as Leads. Test with one click after saving.
                  </p>
                </div>

                {/* CAPI Access Token — arms closed-deal reporting */}
                <div style={{ marginTop: '1.25rem', marginBottom: '0' }}>
                  <label style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Conversions API Access Token: arms closed-deal reporting
                    <HelpPopover {...HELP_CONTENT.meta_accessToken} />
                  </label>
                  {(() => {
                    const metaTokenSaved = !formData.accessToken &&
                      pixels.some((p) => p.platform === 'meta' && (p.hasAccessToken || p.accessToken));

                    // Current pixel config for guided step (pixel ID without the raw token)
                    const currentPixelConfig = {
                      pixelId: formData.pixelId,
                      platform: 'meta',
                    };

                    return (
                      <>
                        {/* Raw token input — always available for users who already have a token */}
                        <p style={{ fontSize: '0.8rem', color: 'var(--muted)', margin: '0 0 0.5rem' }}>
                          Events Manager → your pixel → Settings → Conversions API → Generate access token
                        </p>
                        <input
                          type="password"
                          value={formData.accessToken}
                          onChange={(e) => handleInputChange('accessToken', e.target.value)}
                          placeholder={metaTokenSaved
                            ? 'Token saved, paste to replace'
                            : 'Paste your CAPI token to report closed-won deals back to Meta'}
                          className="fb-input" style={{ width: '100%', fontSize: '0.9rem',
                          }}
                        />
                        {metaTokenSaved && (
                          <p style={{ fontSize: '0.85rem', color: '#1b5e20', marginTop: '0.4rem', marginBottom: 0 }}>
                            🔒 Token secured. Closed-deal reporting armed. Paste a new token above to replace it.
                          </p>
                        )}
                        {formData.pixelId && !formData.accessToken && !metaTokenSaved && (
                          <p style={{ fontSize: '0.85rem', color: '#b45309', marginTop: '0.4rem', marginBottom: 0 }}>
                            ⚠ Closed-deal reporting not armed yet.
                          </p>
                        )}
                        {formData.accessToken && (
                          <p style={{ fontSize: '0.85rem', color: '#2e7d32', marginTop: '0.4rem', marginBottom: 0 }}>
                            ✓ Will arm closed-deal reporting, run Test to confirm.
                          </p>
                        )}

                        {/* Guided walkthrough — shown when no token is saved or entered */}
                        {!metaTokenSaved && !formData.accessToken && formData.pixelId && (
                          <div style={{ marginTop: '0.75rem' }}>
                            <button
                              type="button"
                              onClick={() => setShowMetaGuide((v) => !v)}
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                color: '#1a73e8',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                textDecoration: 'underline',
                              }}
                            >
                              {showMetaGuide ? 'Hide walkthrough' : 'Walk me through it →'}
                            </button>
                            {showMetaGuide && (
                              <GuidedManualStep
                                guideId="meta_capi_token"
                                pixelConfig={currentPixelConfig}
                                formId={formId}
                                onComplete={(updatedPixelConfig) => {
                                  // Mirror what TrackingConfig does after setFormSecret:
                                  // surface hasAccessToken:true so the UI updates without a reload.
                                  handleInputChange('accessToken', '');
                                  setShowMetaGuide(false);
                                  // Propagate the update upward so the pixel list reflects the new state
                                  if (onSave) {
                                    const updatedPixels = pixels.map((p) =>
                                      p.platform === 'meta'
                                        ? { ...p, hasAccessToken: true }
                                        : p
                                    );
                                    onSave(updatedPixels);
                                  }
                                }}
                              />
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}
                  <p style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '0.5rem', marginBottom: 0 }}>
                    This is the part competitors can't do: when you mark a deal Won, we report the real conversion value back to Meta. Without it, your form still tracks submits, but closed-won deals won't report back.
                  </p>
                </div>
                </>
              )}

              {/* LinkedIn Fields */}
              {selectedPlatform === 'linkedin' && (
                <>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Conversion ID
                      <HelpPopover {...HELP_CONTENT.linkedin_conversionId} />
                      <span style={{ fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                        (Optional - if using Insight Tag code, leave blank)
                      </span>
                    </label>
                    <input
                      type="text"
                      value={formData.conversionId}
                      onChange={(e) => handleInputChange('conversionId', e.target.value)}
                      placeholder={instructions.exampleConversionId}
                      className={`fb-input${validationErrors.conversionId ? ' fb-input-error' : ''}`}
                    />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Partner ID
                      <HelpPopover {...HELP_CONTENT.linkedin_partnerId} />
                      <span style={{ fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                        (Required if using Conversion ID)
                      </span>
                    </label>
                    <input
                      type="text"
                      value={formData.partnerId}
                      onChange={(e) => handleInputChange('partnerId', e.target.value)}
                      placeholder={instructions.examplePartnerId}
                      className={`fb-input${validationErrors.partnerId ? ' fb-input-error' : ''}`}
                    />
                    {validationErrors.partnerId && (
                      <div className="fb-inline-error" style={{ marginTop: '0.25rem' }}>
                        {validationErrors.partnerId}
                      </div>
                    )}
                  </div>

                  {/* Guided walkthrough — shown when Conversion ID is empty */}
                  {!formData.conversionId && (
                    <div style={{ marginBottom: '1rem' }}>
                      <button
                        type="button"
                        onClick={() => setShowLinkedInGuide((v) => !v)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          color: '#1a73e8',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          textDecoration: 'underline',
                        }}
                      >
                        {showLinkedInGuide ? 'Hide walkthrough' : 'Walk me through it →'}
                      </button>
                      {showLinkedInGuide && (
                        <GuidedManualStep
                          guideId="linkedin_conversion"
                          pixelConfig={{ platform: 'linkedin' }}
                          formId={formId}
                          onComplete={() => setShowLinkedInGuide(false)}
                        />
                      )}
                    </div>
                  )}
                </>
              )}

              {/* TikTok Fields — Simple: Pixel ID only. Access token in Advanced. */}
              {selectedPlatform === 'tiktok' && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Pixel ID <span style={{ color: 'var(--danger)' }}>*</span>
                    <HelpPopover {...HELP_CONTENT.tiktok_pixelId} />
                    <span style={{ fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                      (19 characters)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={formData.pixelId}
                    onChange={(e) => handleInputChange('pixelId', e.target.value)}
                    placeholder={instructions.examplePixelId}
                    className={`fb-input${validationErrors.pixelId ? ' fb-input-error' : ''}`}
                  />
                  {validationErrors.pixelId && (
                    <div className="fb-inline-error" style={{ marginTop: '0.25rem' }}>
                      {validationErrors.pixelId}
                    </div>
                  )}

                  {/* Guided walkthrough — shown when Pixel ID is empty */}
                  {!formData.pixelId && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <button
                        type="button"
                        onClick={() => setShowTiktokGuide((v) => !v)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          color: '#1a73e8',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          textDecoration: 'underline',
                        }}
                      >
                        {showTiktokGuide ? 'Hide walkthrough' : 'Walk me through it →'}
                      </button>
                      {showTiktokGuide && (
                        <GuidedManualStep
                          guideId="tiktok_pixel"
                          pixelConfig={{ platform: 'tiktok' }}
                          formId={formId}
                          onComplete={() => setShowTiktokGuide(false)}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
              </>
              )}

              {/* Advanced: event names & options — hidden for Meta (token moved to primary config) */}
              <div style={{ marginBottom: '1rem', display: selectedPlatform === 'meta' ? 'none' : undefined }}>
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  style={{
                    background: showAdvanced ? '#e9ecef' : 'transparent',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.9rem',
                    color: 'var(--muted)',
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'left',
                    fontWeight: '500',
                  }}
                >
                  {showAdvanced ? '▼' : '▶'} Advanced: event names & options
                </button>
                {showAdvanced && (
                  <div className="fb-callout info" style={{ marginTop: '0.75rem', padding: '1rem' }}>
                    <p style={{ fontSize: '0.85rem', color: 'var(--muted)', marginTop: 0, marginBottom: '1rem', lineHeight: 1.5 }}>
                      <strong>Platform UIs change often.</strong> If the “where to find it” path doesn’t match your screen, look for <strong>Conversions</strong>, <strong>Events</strong>, or <strong>Pixels</strong>. The ID or tag you need is the same.
                    </p>
                    {selectedPlatform !== 'meta' && (
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                          Event Name <span style={{ color: 'var(--danger)' }}>*</span>
                          <span style={{ fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                            (Unique per form for remarketing)
                          </span>
                        </label>
                        <input
                          type="text"
                          value={formData.eventName}
                          onChange={(e) => handleInputChange('eventName', e.target.value)}
                          onFocus={(e) => {
                            if (!e.target.value) {
                              handleInputChange('eventName', suggestEventName());
                            }
                          }}
                          placeholder={suggestEventName()}
                          className={`fb-input${validationErrors.eventName ? ' fb-input-error' : ''}`}
                        />
                        {validationErrors.eventName && (
                          <div className="fb-inline-error" style={{ marginTop: '0.25rem' }}>
                            {validationErrors.eventName}
                          </div>
                        )}
                        <p style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '0.25rem', marginBottom: 0 }}>
                          Use unique names per form (e.g. Lead_Homepage, Lead_Services).
                        </p>
                      </div>
                    )}
                    {selectedPlatform === 'tiktok' && (
                      <div style={{ marginBottom: '0.5rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem', fontWeight: '500' }}>
                          Access Token <HelpPopover {...HELP_CONTENT.tiktok_accessToken} />
                          <span style={{ fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 'normal' }}>(Optional)</span>
                        </label>
                        {(() => {
                          const tiktokTokenSaved = !formData.accessToken &&
                            pixels.some((p) => p.platform === 'tiktok' && (p.hasAccessToken || p.accessToken));
                          const tiktokPixelConfig = { pixelId: formData.pixelId, platform: 'tiktok' };
                          return (
                            <>
                              <input
                                type="password"
                                value={formData.accessToken}
                                onChange={(e) => handleInputChange('accessToken', e.target.value)}
                                placeholder={tiktokTokenSaved
                                  ? 'Token saved, paste to replace'
                                  : 'Optional: for one-click test'}
                                className="fb-input" style={{ width: '100%', fontSize: '0.9rem',
                                }}
                              />
                              {tiktokTokenSaved
                                ? <p style={{ fontSize: '0.8rem', color: '#1b5e20', marginTop: '0.25rem', marginBottom: 0 }}>🔒 Token secured.</p>
                                : <p style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '0.25rem', marginBottom: 0 }}>Optional. Add to enable one-click testing from here.</p>
                              }

                              {/* Guided walkthrough — shown when no token is saved or entered and Pixel ID exists */}
                              {!tiktokTokenSaved && !formData.accessToken && formData.pixelId && (
                                <div style={{ marginTop: '0.75rem' }}>
                                  <button
                                    type="button"
                                    onClick={() => setShowTiktokTokenGuide((v) => !v)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      padding: 0,
                                      color: '#1a73e8',
                                      cursor: 'pointer',
                                      fontSize: '0.85rem',
                                      textDecoration: 'underline',
                                    }}
                                  >
                                    {showTiktokTokenGuide ? 'Hide walkthrough' : 'Walk me through it →'}
                                  </button>
                                  {showTiktokTokenGuide && (
                                    <GuidedManualStep
                                      guideId="tiktok_capi_token"
                                      pixelConfig={tiktokPixelConfig}
                                      formId={formId}
                                      onComplete={() => {
                                        handleInputChange('accessToken', '');
                                        setShowTiktokTokenGuide(false);
                                        if (onSave) {
                                          const updatedPixels = pixels.map((p) =>
                                            p.platform === 'tiktok'
                                              ? { ...p, hasAccessToken: true }
                                              : p
                                          );
                                          onSave(updatedPixels);
                                        }
                                      }}
                                    />
                                  )}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Auto-Update Toggle - Hidden for now */}
              <input type="hidden" value={false} />

              {/* Actions */}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="inbox-pill-btn-primary"
                  onClick={handleAddPixel}
                  style={{ fontSize: '0.9rem' }}
                >
                  {editIndex !== null ? 'Save Changes' : 'Save Tracking'}
                </button>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setEditIndex(null);
                    setSelectedPlatform('');
                    setImplementationMethod('direct');
                    setFormData({
                      conversionId: '',
                      conversionLabel: '',
                      pixelId: '',
                      partnerId: '',
                      accessToken: '',
                      eventName: '',
                      gtmContainerId: '',
                      autoUpdate: false,
                    });
                    setValidationErrors({});
                  }}
                  className="inbox-pill-btn"
                  style={{ fontSize: '0.9rem' }}
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
